import { RouterProvider } from '@tanstack/react-router'
import { ThemeProvider, ToastProvider, useDocumentIdentity } from '@solum/ui'
import { SolumProvider } from './data/client'
import { WriteFailureToaster } from './data/WriteFailure'
import { AuthProvider, ResetPassword, SignIn, authClient, pendingSwitchEmail, useAuth } from '@solum/auth'
import { useState } from 'react'
import { BrandSync } from './app/BrandSync'
import { brandDoc, brandSlots, brandState, useDeploymentBrand } from './app/ConsoleBrand'
import { CONSOLE_RECOVERY_PATH, consoleRecoveryUrl, isConsoleRecoveryArrival } from './app/consoleRecovery'
import { router } from './app/router'
import { DependencyDegradation } from './app/DependencyDegradation'

/**
 * The authentication gate (SEC-03).
 *
 * Against a real deployment nothing renders until someone has signed in — every
 * request carries that person's token, and `auth.uid()` is what RLS filters and
 * audit rows are written against. In fixture mode there is no server to
 * authenticate against, so the app renders as before; the persona switcher is
 * the identity model there.
 *
 * The gate lives INSIDE SolumProvider so a signed-in app keeps one query client
 * across a session refresh, and outside the router so an expired session cannot
 * leave a half-rendered page behind.
 */
function Gate() {
  const { isLive, session } = useAuth()
  /* The deployment's signed-out identity (#2296). Read before either screen
     renders, and never allowed to block one: an unconfigured deployment, an
     unknown host or an unreachable database all resolve to `null`, which is the
     honest generic card. Somebody signing in to FIX a broken brand must not be
     stopped by the broken brand. */
  const brand = useDeploymentBrand()
  const publicBrand = brandDoc(brand)
  const identity = brandSlots(publicBrand)
  useDocumentIdentity({
    title: publicBrand?.name,
    fallbackTitle: 'Marble',
    favicon: publicBrand?.favicon,
  })
  /* The outcome, on the page. A branding read must never block the gate, so
     every failure ends at the generic card — which means the card alone cannot
     tell "this deployment declared no identity" from "the read never worked".
     Marking the state is what stops the fallback hiding broken wiring, and it
     is what the browser proof asserts rather than inferring from pixels. */
  const brandMark = { 'data-brand-state': brandState(brand) }
  // Captured once before paint. ResetPassword performs the matching read+strip
  // of the bearer fragment; this state only selects the correct shared screen.
  const [resetting, setResetting] = useState(() => isConsoleRecoveryArrival())
  const done = () => {
    if (typeof window !== 'undefined' && window.location.pathname === CONSOLE_RECOVERY_PATH) {
      window.history.replaceState(null, '', '/')
    }
    setResetting(false)
  }

  // A recovery arrival outranks an old session: resetting after compromise
  // must revoke that session rather than dropping the person into the app.
  if (isLive && resetting) {
    return <div {...brandMark} style={{ display: 'contents' }}><ResetPassword
      {...identity}
      onRequest={(email) => authClient.requestPasswordRecovery(email, consoleRecoveryUrl())}
      onDone={done}
    /></div>
  }
  /* An account switch is a sign-out followed by a fresh authentication (#1053
     4b), so the switcher lands HERE — there is no second screen. The address
     the tab is on its way to pre-fills the form; it is read at render rather
     than held in state because a reload of this tab must not lose it, and it
     lives in `sessionStorage`, so a switch started in one tab never pre-fills
     another tab's form with an address nobody there chose. */
  if (isLive && !session) {
    return <div {...brandMark} style={{ display: 'contents' }}><SignIn
      {...identity}
      onForgotPassword={() => setResetting(true)}
      defaultEmail={pendingSwitchEmail()}
    /></div>
  }
  return (
    <>
      <BrandSync />
      <RouterProvider router={router} />
    </>
  )
}

export function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        {/* Inside <ToastProvider> and outside everything else: the mutation
            cache is built at module scope and cannot call useToast(), so a
            refused write reaches the user through this one registered sink
            (#877). Renders nothing. */}
        <WriteFailureToaster />
        <AuthProvider>
          <SolumProvider>
            {/* ONE region, mounted for the whole session and above every screen
                (#1790). Hoisted to here rather than living inside `AppShell`
                because a live region has to be in the accessibility tree BEFORE
                its text arrives — and `Gate` swaps between SignIn, ResetPassword
                and the router, so a region inside the shell was torn down and
                re-inserted on each of those transitions, which is the one shape
                assistive technology does not announce. Renders nothing while the
                deployment is healthy. */}
            <DependencyDegradation />
            <Gate />
          </SolumProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}
